import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { InfoCard } from "@/components/toggles";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatError } from "@/lib/api";
import { ArrowLeft, ChevronDown, Loader2, ShieldCheck, X, AlertTriangle } from "lucide-react";

const AREAS = [
  {
    key: "stilltouched", label: "stilltouched",
    roles: [
      { code: "ST_USER", label: "stilltouched User", desc: "Basis-Social-Nutzung" },
      { code: "ST_CREATOR", label: "stilltouched Creator", desc: "Eigener Feed & Stories als Creator" },
    ],
  },
  {
    key: "marketplace", label: "Marketplace",
    roles: [
      { code: "MP_BUYER", label: "Marketplace Buyer", desc: "Im Marktplatz kaufen" },
      { code: "MP_SELLER", label: "Marketplace Seller", desc: "Im Marktplatz verkaufen" },
    ],
  },
  {
    key: "stilltouchedX", label: "stilltouchedX",
    roles: [
      { code: "STX_BUYER", label: "stilltouchedX Buyer", desc: "In stilltouchedX kaufen (ohne AVS teilweise unscharf)" },
      { code: "STX_SELLER", label: "stilltouchedX Seller", desc: "In stilltouchedX verkaufen – zugleich STX Creator" },
    ],
  },
];

const DATA_REQUIRED = ["MP_BUYER", "MP_SELLER", "STX_BUYER", "STX_SELLER"];

// Schieberegler (gleiche Optik wie unsere anderen Toggles)
function Toggle({ on, busy, onClick, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      disabled={busy}
      role="switch"
      aria-checked={on}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${on ? "bg-[#0A0A0A]" : "bg-[#E5E5E5]"}`}
    >
      {busy ? (
        <Loader2 size={12} className="animate-spin mx-auto text-white" />
      ) : (
        <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${on ? "translate-x-5" : "translate-x-0.5"}`} />
      )}
    </button>
  );
}

export default function AccessPage() {
  const { t } = useTranslation();
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [caps, setCaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyCode, setBusyCode] = useState(null);
  const [error, setError] = useState("");
  const [personalDataComplete, setPersonalDataComplete] = useState(true);
  const [missingFields, setMissingFields] = useState([]);
  const [open, setOpen] = useState({ stilltouched: true, marketplace: false, stilltouchedX: false });
  const [confirm, setConfirm] = useState(null); // { code, count, role }

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/profile/access");
      setCaps(data.capabilities || []);
      setPersonalDataComplete(!!data.personal_data_complete);
      setMissingFields(data.personal_data_missing_fields || []);
    } catch { setCaps([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!user || user === false) { navigate("/login"); return; }
    load();
  }, [user, navigate, load]);

  const statusOf = (code) => caps.find(c => c.capability_code === code)?.status || "NOT_ENABLED";
  const isActive = (code) => statusOf(code) === "ACTIVE";
  const avsVerified = user?.age_verification_status === "VERIFIED";

  // Was blockiert die Aktivierung? → null = aktivierbar, sonst Grund-Objekt
  const blockReason = (code) => {
    if (DATA_REQUIRED.includes(code) && !personalDataComplete) {
      return { type: "DATA", label: "Profildaten erforderlich", action: "Profil vervollständigen", onAction: () => navigate("/profile/edit") };
    }
    if (code === "STX_SELLER" && !avsVerified) {
      return { type: "AVS", label: "AVS erforderlich", action: "AVS starten", onAction: () => navigate("/profile/security") };
    }
    return null;
  };

  const statusLabel = (code) => {
    const s = statusOf(code);
    if (s === "ACTIVE") return "Aktiv";
    if (s === "PENDING_REQUIREMENTS") return "E-Mail-Bestätigung erforderlich";
    if (s === "SUSPENDED") return "Gesperrt";
    if (s === "REVOKED") return "Entzogen";
    const b = blockReason(code);
    if (b) return b.label;
    return "Nicht aktiv";
  };

  const activate = async (code) => {
    setError("");
    const b = blockReason(code);
    if (b) { setError(`${b.label}. ${b.action} erforderlich.`); return; }
    setBusyCode(code);
    try {
      await api.post("/profile/access/activate", { capability_code: code });
      await load();
      await refreshUser();
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : (d?.message || formatError(err)));
    } finally { setBusyCode(null); }
  };

  const deactivate = async (code, confirmCancel = false) => {
    setError("");
    setBusyCode(code);
    try {
      await api.post("/profile/access/deactivate", { capability_code: code, confirm_cancel_orders: confirmCancel });
      setConfirm(null);
      await load();
      await refreshUser();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 409 && d?.code === "ACTIVE_ORDERS") {
        setConfirm({ code, count: d.count, role: d.role });
      } else {
        setError(typeof d === "string" ? d : (d?.message || formatError(err)));
      }
    } finally { setBusyCode(null); }
  };

  const onToggle = (code) => {
    if (isActive(code)) deactivate(code, false);
    else activate(code);
  };

  if (loading) return <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-[#E5E5E5] border-t-[#0A0A0A] rounded-full animate-spin" /></div>;

  return (
    <div data-testid="access-page">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E5E5E5]">
        <button data-testid="back-btn" onClick={() => navigate(-1)} className="text-[#0A0A0A]"><ArrowLeft size={20} /></button>
        <h1 className="text-base font-bold text-[#0A0A0A]">Meine Bereiche verwalten</h1>
      </div>

      {error && <div className="mx-4 mt-4"><InfoCard variant="danger" size="sm" testId="access-error">{error}</InfoCard></div>}

      {!personalDataComplete && (
        <InfoCard variant="warning" testId="personal-data-required-banner" className="mx-4 mt-4">
          <p className="text-xs font-bold text-amber-900 mb-1">Persönliche Daten unvollständig</p>
          <p className="text-[11px] text-amber-800 leading-relaxed">
            Für Käufer-/Verkäufer-Bereiche (Marketplace / stilltouchedX) brauchst du zuerst vollständige
            persönliche Daten (Vor-/Nachname und komplette Adresse).
          </p>
          {missingFields.length > 0 && (
            <p className="text-[10px] text-amber-700 mt-1">
              Fehlend: {missingFields.map(f => ({
                first_name: "Vorname", last_name: "Nachname", address_street: "Straße",
                address_house_number: "Hausnummer", address_zip: "PLZ", address_city: "Stadt", address_country: "Land",
              }[f] || f)).join(", ")}
            </p>
          )}
          <button data-testid="goto-profile-edit" onClick={() => navigate("/profile/edit")}
            className="mt-2 h-8 px-3 rounded-full bg-amber-600 text-white text-xs font-medium hover:bg-amber-500">
            Profil vervollständigen →
          </button>
        </InfoCard>
      )}

      <div className="px-4 py-4 space-y-3">
        {AREAS.map((area) => {
          const expanded = !!open[area.key];
          const activeCount = area.roles.filter(r => isActive(r.code)).length;
          return (
            <div key={area.key} className="border border-[#E5E5E5] rounded-xl overflow-hidden">
              <button
                data-testid={`area-toggle-${area.key}`}
                onClick={() => setOpen(o => ({ ...o, [area.key]: !o[area.key] }))}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#FAFAFA] transition-colors"
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-[#0A0A0A]">{area.label}</span>
                  <span className="text-[10px] text-[#999]">{activeCount}/{area.roles.length} aktiv</span>
                </div>
                <ChevronDown size={18} className={`text-[#999] transition-transform ${expanded ? "rotate-180" : ""}`} />
              </button>

              {expanded && (
                <div className="divide-y divide-[#F0F0F0] border-t border-[#F0F0F0]">
                  {area.roles.map((role) => {
                    const active = isActive(role.code);
                    const b = !active ? blockReason(role.code) : null;
                    const busy = busyCode === role.code;
                    return (
                      <div key={role.code} data-testid={`access-cap-${role.code}`} className="px-4 py-3">
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-[#0A0A0A]">{role.label}</p>
                            <p className="text-xs text-[#999] mt-0.5">{role.desc}</p>
                          </div>
                          <div className="flex flex-col items-end gap-1 shrink-0">
                            <Toggle on={active} busy={busy} onClick={() => onToggle(role.code)} testId={`toggle-${role.code}`} />
                            <span data-testid={`status-${role.code}`} className={`text-[10px] font-medium ${active ? "text-green-600" : b ? "text-amber-600" : "text-[#999]"}`}>
                              {statusLabel(role.code)}
                            </span>
                          </div>
                        </div>

                        {/* AVS / Profildaten-Hinweis mit Aktion */}
                        {b && (
                          <button
                            data-testid={`block-action-${role.code}`}
                            onClick={b.onAction}
                            className="mt-2 inline-flex items-center gap-1 h-8 px-3 rounded-full border border-[#0A0A0A] text-[#0A0A0A] text-xs font-medium hover:bg-[#F5F5F5]"
                          >
                            {b.type === "AVS" && <ShieldCheck size={13} />} {b.action} →
                          </button>
                        )}

                        {/* KYC-Platzhalter für STX Seller (Ondato, bald verfügbar) */}
                        {role.code === "STX_SELLER" && (
                          <p className="mt-2 text-[10px] text-[#999] flex items-center gap-1">
                            <ShieldCheck size={11} /> KYC-Verifizierung (Ondato): bald verfügbar – wird später für Auszahlungen benötigt.
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Deaktivierungs-Bestätigung bei aktiven Bestellungen */}
      {confirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div data-testid="deactivate-confirm-modal" className="bg-white rounded-xl max-w-sm w-full shadow-xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#E5E5E5]">
              <h3 className="font-semibold text-[#0A0A0A] flex items-center gap-2"><AlertTriangle size={16} className="text-amber-500" /> Aktive Bestellungen</h3>
              <button onClick={() => setConfirm(null)} className="text-[#666] hover:text-[#0A0A0A]"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-[#333]">
                In diesem Bereich gibt es noch <strong>{confirm.count}</strong> aktive Bestellung(en).
                Wenn du den Bereich jetzt deaktivierst, {confirm.role === "SELLER" ? "werden deine aktiven Verkäufe storniert" : "werden deine aktiven Käufe storniert"} und automatisch <strong>gutgeschrieben</strong>.
              </p>
              <p className="text-xs text-[#666]">Bist du sicher, dass du fortfahren möchtest?</p>
              <div className="flex items-center justify-end gap-2 pt-1">
                <button onClick={() => setConfirm(null)} className="px-4 py-2 text-sm text-[#666] hover:text-[#0A0A0A]">Abbrechen</button>
                <button
                  data-testid="deactivate-confirm-btn"
                  onClick={() => deactivate(confirm.code, true)}
                  disabled={busyCode === confirm.code}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-red-600 hover:bg-red-500 text-white rounded-full disabled:opacity-50"
                >
                  {busyCode === confirm.code ? <Loader2 size={14} className="animate-spin" /> : null} Stornieren & deaktivieren
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
